// The platform seam (BACKLOG #17): everything that differs between the web and
// a future native shell goes through here. Today there is one implementation,
// the web one. Capacitor adds a second one later without touching screens.

export interface Platform {
  storage: {
    get(key: string): string | null;
    set(key: string, value: string): void;
    remove(key: string): void;
    /** Every key held, so account data can be cleared by prefix. */
    keys(): string[];
  };
  /** Opens the share sheet, or copies the link where there is none. Resolves to what happened. */
  share(data: { title: string; url: string }): Promise<"shared" | "copied" | "failed">;
  copy(text: string): Promise<boolean>;
  /** A short tap of haptic feedback. Android web only; iPhone web cannot vibrate (research #1). */
  haptic(): void;
  reducedMotion(): boolean;
}

// Storage access can throw (private mode, blocked site data), and nothing the
// app keeps there may be the only copy of something important (TypeFire: a
// webview's storage was not durable).
const memory = new Map<string, string>();

export const web: Platform = {
  storage: {
    get(key) {
      try {
        return localStorage.getItem(key) ?? memory.get(key) ?? null;
      } catch {
        return memory.get(key) ?? null;
      }
    },
    set(key, value) {
      memory.set(key, value);
      try {
        localStorage.setItem(key, value);
      } catch {
        // memory keeps it for this page
      }
    },
    remove(key) {
      memory.delete(key);
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
    keys() {
      const all = new Set(memory.keys());
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k) all.add(k);
        }
      } catch {
        // memory is all there is
      }
      return [...all];
    },
  },
  async share({ title, url }) {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return "shared";
      }
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return "failed";
    }
    return (await web.copy(url)) ? "copied" : "failed";
  },
  async copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },
  haptic() {
    try {
      navigator.vibrate?.(12);
    } catch {
      // unsupported
    }
  },
  reducedMotion() {
    return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  },
};

export const platform: Platform = web;
